import { Button, Divider, Input, Table, Tooltip } from "antd";
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from "react-redux";
import Action from "../../components/Menu/Action";
import { openPopup2, POPUP_TYPE } from "../../contexts/PopupContext";
import { ChecklistService } from "../../util/services/checklist";
import { selectChecklist } from "../../util/store/checklist/constants";
import { checklistActions } from "../../util/store/checklist/reducer";
import { selectPermissions } from "../../util/store/selectors";
import { useSelector } from "../../util/store/store";
import {
    eventType,
    KEY_SPLIT_CHECKLIST,
    OPERATION_TYPE,
    TASK_TYPE,
    taskTypes,
    treatmentType,
    vesselType
} from "../../util/varibles/constants";
import { IChecklist } from "../../util/varibles/interface";
import { ACTION } from "../../util/varibles/permission";
import PopupEdit from "./PopupEdit";
import styles from "./style.module.scss";

export interface IViewStep {
    data: { id: string, opType: string, taskType: string, treatmentType: string }
}

export const Names: { [id: string]: (props: IViewStep) => string } = {
    [OPERATION_TYPE.HARVEST]: () => `Harvest`,
    [OPERATION_TYPE.TRANSPORT]: () => `Transport`,
    [OPERATION_TYPE.SERVICE]: () => `Service`,
    [OPERATION_TYPE.TREATMENT]: ({ data }) => {
        if (data.taskType === `${TASK_TYPE.TREATMENT}`) {
            return treatmentType[data.treatmentType]?.name
        } else if (data.taskType === `${TASK_TYPE.MOVE}`) {
            return taskTypes[TASK_TYPE.MOVE]?.name
        }
        return ""
    },
    [OPERATION_TYPE.EVENT]: ({ data }) => {
        return eventType[data.taskType]?.name
    }
}

const SearchTags: React.FC = () => {
    return <div>

    </div>
}


const Checklists: React.FC = () => {
    const [canUpdateChecklist, canDeleteChecklist, canCreateChecklist] = useSelector(selectPermissions([
        ACTION.CHECKLIST.UPDATE,
        ACTION.CHECKLIST.DELETE,
        ACTION.CHECKLIST.CREATE,
    ]));
    const lists = useSelector(selectChecklist.data);
    const loading = useSelector(selectChecklist.loading);
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');

    const handleKeyDown = (e: any) => {
        if (e.key === 'Escape')
            setSearch('')
    }

    const handleClick = useCallback((data?: IChecklist) => {
        let modalProps, onOk;
        if (data) {
            modalProps = { title: 'Update checklist: ' + (data.name || "") };
            onOk = (value: IChecklist) => dispatch(checklistActions.update(value));
        } else {
            modalProps = { title: "New checklist" };
            onOk = (value: IChecklist) => dispatch(checklistActions.add(value));
        }
        openPopup2(<PopupEdit data={data} onSave={onOk} />, {
            modalProps: { ...modalProps, width: "100%" },
            popupType: POPUP_TYPE.DRAWER
        });
    }, [dispatch]);

    const handleAfterDelete = (id: string) => {
        dispatch(checklistActions.delete(id))
    }

    const dataSource = useMemo(() => lists.filter(({ name = "" }) => {
        return search.length === 0 || name.toLowerCase().includes(search.trim().toLowerCase())
    }), [lists, search]);

    const isUpdate = canUpdateChecklist;
    const isDelete = canDeleteChecklist;

    return <div className={styles.content}>
        <div className='flex justify-end mb-10'>
            <SearchTags />
            <Input
                value={search}
                onChange={(e: any) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder='Search by name'
                className={styles.search}
            />
            {canCreateChecklist && <>
                <Divider className='divider-vertical' orientation="vertical" />
                <Tooltip title="Add checklist">
                    <Button className='bt-primary bt-plus' type="primary" shape="circle"
                        onClick={() => handleClick()}
                    />
                </Tooltip>
            </>}
        </div>
        <Table {...{
            rowKey: "id",
            pagination: false,
            columns: [
                {
                    title: 'Name',
                    key: 'name',
                    render: (value: IChecklist) => {
                        return isUpdate ? <div className="link" onClick={() => handleClick(value)}>
                            {value.name}
                        </div> : value.name
                    }
                },
                {
                    title: 'Assigned to',
                    dataIndex: 'required_map',
                    key: "required_map",
                    className: 'column-action',
                    render: (value: IChecklist['required_map']) => {
                        const { vessel_types = [], vessel_names = [], sub_types = [] } = value || {};
                        const names = Array.from(new Set([
                            ...vessel_types.map(item => {
                                const [, type] = item.split(KEY_SPLIT_CHECKLIST);
                                return vesselType[type]?.name;
                            }),
                            ...vessel_names.map(item => item.name),
                            ...sub_types.map(item => {
                                const [, opType, taskType, treatmentType] = item.split(KEY_SPLIT_CHECKLIST);
                                const { [opType]: Name } = Names;
                                return Name?.({ data: { opType, taskType, treatmentType, id: item } });
                            })
                        ].filter(name => name)));

                        return <div className="flex flex-wrap gap-2 pl-3">
                            {names.map(name => {
                                return <div key={name} className={styles.tag}>
                                    {name}
                                </div>
                            })}
                        </div>
                    }
                },
                {
                    key: 'action',
                    width: 50,
                    title: ' ',
                    className: 'column-action',
                    render: (data: IChecklist) => <div className="pl-1 py-1">
                        <Action<IChecklist>
                            data={data}
                            onEdit={isUpdate ? () => handleClick(data) : undefined}
                            onDelete={isDelete ? ChecklistService.delete : undefined}
                            afterDelete={isDelete ? handleAfterDelete : undefined}
                        />
                    </div>
                }
            ],
            loading,
            dataSource
        }} />
    </div>;
};

export default Checklists;
